/**
 * Compact "System Status" pill. Two visual variants:
 *  - variant="footer": always visible on dark backgrounds (marketing footer)
 *  - variant="navbar": only rendered when there IS degradation, on a light
 *    background near the top nav.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useSystemStatus, statusTone } from "@/hooks/useSystemStatus";

const TONE_META = {
    ok:      { dot: "bg-emerald-500", ring: "ring-emerald-400/40", label: "All systems normal" },
    warn:    { dot: "bg-amber-500",   ring: "ring-amber-400/40",   label: "Degraded"           },
    bad:     { dot: "bg-rose-500",    ring: "ring-rose-400/40",    label: "Outage detected"    },
    unknown: { dot: "bg-slate-400",   ring: "ring-slate-400/40",   label: "Status unknown"     },
};

/** Footer variant — always visible, dark background. */
function FooterPill({ tone }) {
    const m = TONE_META[tone];
    return (
        <Link
            to="/status"
            data-testid="footer-status-pill"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
            <span className={`relative inline-flex h-2 w-2`}>
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${m.dot}`} />
                <span className={`relative inline-flex h-2 w-2 rounded-full ${m.dot}`} />
            </span>
            <span>System status: {m.label}</span>
        </Link>
    );
}

/** Navbar variant — only rendered for tone !== "ok" (and != "unknown"). */
function NavbarPill({ tone }) {
    const m = TONE_META[tone];
    const border = tone === "bad"  ? "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                : tone === "warn" ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100";
    return (
        <Link
            to="/status"
            data-testid="navbar-status-pill"
            className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${border}`}
        >
            <span className={`inline-flex h-1.5 w-1.5 rounded-full ${m.dot}`} />
            {m.label}
        </Link>
    );
}

export default function SystemStatusPill({ variant = "footer" }) {
    const status = useSystemStatus();
    const tone = statusTone(status?.overall);

    if (variant === "navbar") {
        // Silent when everything is fine (or before first fetch resolves).
        if (tone === "ok" || tone === "unknown") return null;
        return <NavbarPill tone={tone} />;
    }
    return <FooterPill tone={tone} />;
}
